// Steps configuration for lecture registration
// Function to create steps configuration with internationalization
export const createStepsConfig = (t) => [
  {
    id: "information",
    title: t("Information"),
    expandable: false,
    expanded: false,
    active: true,
    complete: false,
    requiresCompletion: true,
    validationRules: {
      // No validation needed for information step
    }
  },
  {
    id: "general_conditions",
    title: t("Lecture Conditions"),
    expandable: false,
    expanded: false,
    active: false,
    complete: false,
    requiresCompletion: true,
    validationRules: {
      required: ['generalConditions'],
      skipValidation: true // Uses ValidationProvider
    }
  },
  {
    id: "lecture_type",
    title: t("Lecture Type"),
    expandable: false,
    expanded: false,
    active: false,
    complete: false,
    validationRules: {
      required: ['type']
    }
  },
  {
    id: "details",
    title: t("Lecture Details"),
    expandable: true,
    expanded: true,
    active: false,
    complete: false,
    substeps: [
      { 
        id: "language", 
        title: t("Language"), 
        complete: false, 
        active: false,
        validationRules: {
          required: ['language']
        }
      },
      { 
        id: "title", 
        title: t("Title"), 
        complete: false, 
        active: false,
        validationRules: {
          required: ['title'],
          minLength: { title: 10 }
        }
      },
      { 
        id: "subjects", 
        title: t("Subjects"), 
        complete: false, 
        active: false,
        validationRules: {
          required: ['subjects']
        }
      },
      { 
        id: "description", 
        title: t("Description"), 
        complete: false, 
        active: false,
        validationRules: {
          // Uses ValidationProvider with schema validation
        }
      },
      { 
        id: "topics", 
        title: t("Topics"), 
        complete: false, 
        active: false,
        validationRules: {
          required: ['topics'],
          custom: (data) => {
            const topics = data.topics || [];
            return topics.length >= 1 && topics.length <= 3;
          }
        }
      },
      { 
        id: "speakers", 
        title: t("Speakers"), 
        complete: false, 
        active: false,
        validationRules: {
          required: ['speakers'],
          custom: (data) => data.speakers?.length >= 1
        }
      },
      { 
        id: "image", 
        title: t("Image"), 
        complete: false, 
        active: false,
        validationRules: {
          // Optional step - no validation needed
        }
      },
      { 
        id: "handout", 
        title: t("Handout"), 
        complete: false, 
        active: false,
        validationRules: {
          // Optional step - no validation needed
        }
      },
      { 
        id: "presentation", 
        title: t("Presentation"), 
        complete: false, 
        active: false,
        validationRules: {
          // Optional step - no validation needed
        }
      },
    ],
  },
  // {
  //   id: "marketing",
  //   title: t("Marketing"),
  //   expandable: false,
  //   expanded: false,
  //   active: false,
  //   complete: false,
  //   validationRules: {
  //     // No validation needed for marketing step
  //   }
  // },
  {
    id: "review",
    title: t("Review"),
    expandable: false,
    expanded: false,
    active: false,
    complete: false,
    isLast: true,
    neutralDisplay: true, // Always show neutral state (no green check or red X)
    validationRules: {
      // No validation needed for review step
    }
  },
];

// Legacy export for backward compatibility - will use fallback English titles
export const initialStepsConfig = createStepsConfig((key) => key);

// Helper function to extract all step IDs from configuration
export const getAllStepIds = (stepsConfig) => {
  if (!stepsConfig || !Array.isArray(stepsConfig)) {
    console.warn('getAllStepIds: stepsConfig is not a valid array:', stepsConfig);
    return [];
  }
  
  const stepIds = [];
  
  stepsConfig.forEach(step => {
    if (step.substeps && step.substeps.length > 0) {
      step.substeps.forEach(substep => {
        stepIds.push(substep.id);
      });
    } else {
      stepIds.push(step.id);
    }
  });
  
  return stepIds;
};

// Helper function to get step order from configuration
export const getStepOrder = (stepsConfig) => {
  return getAllStepIds(stepsConfig);
};

// Helper function to create step to main step mapping
export const createStepToMainStepMapping = (stepsConfig) => {
  if (!stepsConfig || !Array.isArray(stepsConfig)) {
    console.warn('createStepToMainStepMapping: stepsConfig is not a valid array:', stepsConfig);
    return {};
  }
  
  const mapping = {};
  
  stepsConfig.forEach(step => {
    if (step.substeps && step.substeps.length > 0) {
      step.substeps.forEach(substep => {
        mapping[substep.id] = step.id;
      });
    } else {
      mapping[step.id] = step.id;
    }
  });
  
  return mapping;
};

// Helper function to get validation rules for a step
export const getValidationRulesForStep = (stepsConfig, stepId) => {
  if (!stepsConfig || !Array.isArray(stepsConfig)) {
    console.warn('getValidationRulesForStep: stepsConfig is not a valid array:', stepsConfig);
    return {};
  }
  
  for (const step of stepsConfig) {
    if (step.substeps && step.substeps.length > 0) {
      const substep = step.substeps.find(sub => sub.id === stepId);
      if (substep) {
        return substep.validationRules || {};
      }
    } else if (step.id === stepId) {
      return step.validationRules || {};
    }
  }
  return {};
};

// Helper function to get step title
export const getStepTitle = (stepsConfig, stepId) => {
  if (!stepsConfig || !Array.isArray(stepsConfig)) {
    console.warn('getStepTitle: stepsConfig is not a valid array:', stepsConfig);
    return stepId;
  }
  
  for (const step of stepsConfig) {
    if (step.substeps && step.substeps.length > 0) {
      const substep = step.substeps.find(sub => sub.id === stepId);
      if (substep) {
        return substep.title;
      }
    } else if (step.id === stepId) {
      return step.title;
    }
  }
  return stepId;
};

// Helper function to get main step title
export const getMainStepTitle = (stepsConfig, mainStepId) => {
  if (!stepsConfig || !Array.isArray(stepsConfig)) {
    console.warn('getMainStepTitle: stepsConfig is not a valid array:', stepsConfig);
    return mainStepId;
  }
  
  const step = stepsConfig.find(s => s.id === mainStepId);
  return step ? step.title : mainStepId;
};

// Helper function to check if a step requires completion before navigation
export const isStepRequiringCompletion = (stepsConfig, stepId) => {
  if (!stepsConfig || !Array.isArray(stepsConfig)) {
    console.warn('isStepRequiringCompletion: stepsConfig is not a valid array:', stepsConfig);
    return false;
  }
  
  for (const step of stepsConfig) {
    if (step.substeps && step.substeps.length > 0) {
      const substep = step.substeps.find(sub => sub.id === stepId);
      if (substep) {
        return substep.requiresCompletion || false;
      }
    } else if (step.id === stepId) {
      return step.requiresCompletion || false;
    }
  }
  return false;
};

// Helper function to get all steps that require completion
export const getStepsRequiringCompletion = (stepsConfig) => {
  if (!stepsConfig || !Array.isArray(stepsConfig)) {
    console.warn('getStepsRequiringCompletion: stepsConfig is not a valid array:', stepsConfig);
    return [];
  }
  
  const requiredSteps = [];
  
  stepsConfig.forEach(step => {
    if (step.substeps && step.substeps.length > 0) {
      step.substeps.forEach(substep => {
        if (substep.requiresCompletion) {
          requiredSteps.push(substep.id);
        }
      });
    } else if (step.requiresCompletion) {
      requiredSteps.push(step.id);
    }
  });
  
  return requiredSteps;
};